/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   Modal เลือกสูตรจากงาน RD = Sample Preparation (spmaster/spdetail)
   เพื่อดึงมาใส่ BOM ของ ERP

   2 ขั้นตอนในหน้าต่างเดียว
     ขั้นที่ 1  เลือกใบ Sample Preparation จากรายการ
     ขั้นที่ 2  ดูรายการวัตถุดิบของใบนั้น พร้อมสถานะจับคู่กับ master ERP
               แถวไหนไม่มีรหัสใน ERP "ต้องกดเลือกวัตถุดิบทดแทนทีละรายการ"
               จนครบก่อน ปุ่มดึงเข้าตาราง BOM ถึงจะกดได้

   ส่งกลับผ่าน values() เป็น array ของแถวที่ mat_code ครบทุกแถวแล้ว
============================================================ */
import React, { useEffect, useState } from "react";
import {
  Modal,
  Card,
  Table,
  message,
  Input,
  Space,
  Spin,
  Alert,
  Button,
  Tag,
  Typography,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import OptionService from "../../../../service/ERP/Options.service";
import ModalMaterial from "../material/ModalMaterial";
import "./modal-rd-bom.css";

const optionService = OptionService();

const norm = (v) => (v ?? "").toString().trim().toLowerCase();
const num = (v) => Number(v || 0);

export default function ModalRdBom({ show, close, values }) {
  const [openModal, setOpenModal] = useState(show);
  const [loading, setLoading] = useState(true);

  const [list, setList] = useState([]); // รายการใบ Sample Preparation
  const [listWrap, setListWrap] = useState([]); // หลังกรองด้วยคำค้น
  const [picked, setPicked] = useState(null); // ใบที่เลือก (ขั้นที่ 2)
  const [detail, setDetail] = useState([]); // วัตถุดิบของใบที่เลือก

  /* วัตถุดิบ ERP ที่ผู้ใช้เลือกทดแทน เก็บเป็น map : rd_code -> material */
  const [picks, setPicks] = useState({});
  /* แถวที่กำลังเลือกทดแทนอยู่ (เปิด ModalMaterial ซ้อน) */
  const [pickingFor, setPickingFor] = useState(null);

  const handleClose = () => setTimeout(() => close(false), 140);

  /* ---------- ขั้นที่ 1 : โหลดรายการใบ Sample Preparation ---------- */
  useEffect(() => {
    if (!openModal) return;
    setLoading(true);
    optionService
      .optionsRdBom({})
      .then((res) => {
        const { data } = res.data;
        setList(data || []);
        setListWrap(data || []);
      })
      .catch(() => message.error("โหลดรายการ Sample Preparation ไม่สำเร็จ"))
      .finally(() => setLoading(false));
  }, [openModal]);

  const handleSearch = (v) => {
    const q = norm(v);
    if (!q) return setListWrap(list);
    setListWrap(
      list.filter(
        (d) =>
          norm(d.spcode).includes(q) ||
          norm(d.spname).includes(q) ||
          norm(d.srcode).includes(q) ||
          norm(d.cusname).includes(q)
      )
    );
  };

  /* ---------- ขั้นที่ 2 : โหลดรายละเอียดใบที่เลือก ---------- */
  const handlePick = (row) => {
    setLoading(true);
    optionService
      .optionsRdBom({ code: row.spcode })
      .then((res) => {
        const { detail: d } = res.data.data || {};
        setPicked(row);
        setDetail(d || []);
        setPicks({}); // เปลี่ยนใบ = ล้างของที่เคยเลือกทดแทนไว้
      })
      .catch(() => message.error("โหลดรายละเอียด Sample Preparation ไม่สำเร็จ"))
      .finally(() => setLoading(false));
  };

  /* แถวนี้พร้อมใช้หรือยัง = จับคู่อัตโนมัติได้ หรือผู้ใช้เลือกทดแทนแล้ว */
  const isReady = (d) => Number(d.matched) === 1 || !!picks[d.rd_code];

  const pendingRows = detail.filter((d) => !isReady(d));
  const allReady = detail.length > 0 && pendingRows.length === 0;

  /* ผู้ใช้เลือกวัตถุดิบ ERP มาทดแทนให้แถวที่กำลังเลือกอยู่ */
  const handleMaterialChoosed = (mat) => {
    if (!pickingFor || !mat?.mat_code) return;

    /* กันเลือกซ้ำกับแถวอื่นในใบเดียวกัน — BOM ไม่ควรมีวัตถุดิบซ้ำ */
    const dup = Object.entries(picks).find(
      ([k, v]) => k !== pickingFor.rd_code && v?.mat_code === mat.mat_code
    );
    const dupAuto = detail.find(
      (d) => Number(d.matched) === 1 && d.mat_code === mat.mat_code
    );
    if (dup || dupAuto) {
      message.warning(`${mat.mat_code} ถูกใช้ในรายการอื่นของสูตรนี้แล้ว`);
      return;
    }

    setPicks((s) => ({ ...s, [pickingFor.rd_code]: mat }));
    /* ไม่ต้อง setPickingFor(null) ตรงนี้ — ModalMaterial ปิดตัวเองแล้วเรียก
       close() กลับมาให้ ถ้าเคลียร์ที่นี่จะไป unmount กลางทางจนแอนิเมชันสะดุด */
  };

  /* ---------- ยืนยัน : แปลงเป็นแถวของตาราง BOM ---------- */
  const handleConfirm = () => {
    if (!allReady) {
      message.error(
        `ยังเหลือ ${pendingRows.length} รายการที่ยังไม่ได้เลือกวัตถุดิบของ ERP`
      );
      return;
    }

    const rows = detail.map((d, i) => {
      /* matched = 1 ใช้ค่าจาก master ERP ที่ join มา
         ไม่ match ใช้ค่าจากวัตถุดิบที่ผู้ใช้เลือกทดแทน */
      const sub = picks[d.rd_code];
      const mat =
        Number(d.matched) === 1
          ? {
              mat_code: d.mat_code,
              mat_name: d.mat_name,
              typename: d.typename,
              unit: d.unit,
              multiply: d.multiply,
              yield: d.yield,
            }
          : sub;

      const mul = num(mat?.multiply) || 1;
      return {
        seq: i + 1,
        mat_code: mat?.mat_code || "",
        mat_name: mat?.mat_name || "",
        typename: mat?.typename || "",
        unit: mat?.unit || d.rd_unit || "",
        /* spdetail.amount = น้ำหนักตัดแต่งแล้ว ตรงกับช่อง qty ของ BOM
           amount_after_lost = น้ำหนักวัตถุดิบสด ตรงกับ qty_after_lost */
        qty: num(d.qty),
        qty_after_lost: num(d.amount_after_lost),
        percent: num(d.percent),
        totalpercent: num(d.totalpercent),
        multiply: mul,
        yield: num(mat?.yield) || 100,
        wt_in_process: num(d.qty) * mul,
        method: d.method || "",
        /* ต้นทางจาก Sample Preparation เก็บไว้ให้ตามรอยได้ */
        rd_code: d.rd_code,
        rd_name: d.rd_name || d.rd_name_th,
        /* ถึงตรงนี้ทุกแถวมี mat_code ครบแล้ว จึงไม่มีแถวค้าง */
        rd_unmatched: false,
        rd_substituted: Number(d.matched) !== 1,
      };
    });

    values(rows);
    setOpenModal(false);
  };

  const listColumns = [
    { title: "รหัส SP", dataIndex: "spcode", key: "spcode", width: 140 },
    { title: "ชื่อตัวอย่าง", dataIndex: "spname", key: "spname" },
    { title: "ลูกค้า", dataIndex: "cusname", key: "cusname", width: 200 },
    {
      title: "วันที่",
      dataIndex: "spdate",
      key: "spdate",
      width: 110,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "จำนวนรายการ",
      dataIndex: "item_qty",
      key: "item_qty",
      width: 110,
      align: "center",
    },
    {
      title: "",
      key: "op",
      width: 90,
      align: "center",
      render: (_, r) => (
        <Button size="small" type="primary" onClick={() => handlePick(r)}>
          เลือก
        </Button>
      ),
    },
  ];

  const detailColumns = [
    {
      title: "สถานะ",
      key: "matched",
      width: 120,
      align: "center",
      render: (_, r) =>
        Number(r.matched) === 1 ? (
          <Tag color="green">พบใน ERP</Tag>
        ) : picks[r.rd_code] ? (
          <Tag color="blue">เลือกทดแทนแล้ว</Tag>
        ) : (
          <Tag color="red">ยังไม่ได้เลือก</Tag>
        ),
    },
    { title: "รหัส (SP)", dataIndex: "rd_code", key: "rd_code", width: 130 },
    {
      title: "ชื่อ (SP)",
      key: "rd_name",
      render: (_, r) => r.rd_name || r.rd_name_th || "-",
    },
    {
      title: "วัตถุดิบใน ERP",
      key: "erp",
      render: (_, r) => {
        if (Number(r.matched) === 1) {
          return (
            <Typography.Text ellipsis className="rd-erp-name">
              {r.mat_code} - {r.mat_name}
            </Typography.Text>
          );
        }
        const sub = picks[r.rd_code];
        /* ชื่อวัตถุดิบยาวมาก ถ้าใช้ Space ปุ่ม "เปลี่ยน" จะถูกดันจนล้นออกนอกคอลัมน์
           จึงใช้ flex : ชื่อย่อด้วย ellipsis (min-width:0) ส่วนปุ่มห้ามหด */
        return sub ? (
          <div className="rd-erp-cell">
            <Typography.Text strong ellipsis className="rd-erp-name">
              {sub.mat_code} - {sub.mat_name}
            </Typography.Text>
            <Button
              size="small"
              className="rd-erp-btn"
              onClick={() => setPickingFor(r)}
            >
              เปลี่ยน
            </Button>
          </div>
        ) : (
          <Button size="small" danger onClick={() => setPickingFor(r)}>
            เลือกวัตถุดิบทดแทน
          </Button>
        );
      },
    },
    {
      title: "จำนวน",
      dataIndex: "qty",
      key: "qty",
      width: 100,
      align: "right",
      render: (v) => num(v).toLocaleString(),
    },
    {
      title: "หน่วย",
      key: "unit",
      width: 90,
      render: (_, r) => picks[r.rd_code]?.unit || r.unit || r.rd_unit || "-",
    },
  ];

  return (
    <>
      <Modal
        open={openModal}
        title={
          picked
            ? `Sample Preparation : ${picked.spcode} — ${picked.spname || ""}`
            : "เลือกสูตรจาก Sample Preparation (RD)"
        }
        afterClose={handleClose}
        onCancel={() => setOpenModal(false)}
        maskClosable={false}
        style={{ top: 20 }}
        width={1100}
        className="modal-rd-bom"
        footer={
          picked ? (
            <Space>
              <Button onClick={() => setPicked(null)}>ย้อนกลับ</Button>
              <Button
                type="primary"
                onClick={handleConfirm}
                /* ยังเลือกทดแทนไม่ครบ กดดึงเข้าไม่ได้ */
                disabled={!allReady}
              >
                {allReady
                  ? "ดึงเข้าตาราง BOM"
                  : `เหลืออีก ${pendingRows.length} รายการ`}
              </Button>
            </Space>
          ) : null
        }
      >
        <Spin spinning={loading}>
          <Space direction="vertical" size="middle" style={{ display: "flex" }}>
            {!picked ? (
              <>
                <Input
                  suffix={<SearchOutlined />}
                  placeholder="ค้นหา รหัส SP / ชื่อตัวอย่าง / รหัสใบขอ / ลูกค้า"
                  onChange={(e) => handleSearch(e.target.value)}
                  allowClear
                />
                <Card style={{ minHeight: "50vh" }}>
                  <Table
                    bordered
                    size="small"
                    rowKey="spcode"
                    dataSource={listWrap}
                    columns={listColumns}
                    pagination={{
                      defaultPageSize: 10,
                      pageSizeOptions: [10, 25, 50],
                    }}
                  />
                </Card>
              </>
            ) : (
              <>
                {pendingRows.length > 0 ? (
                  <Alert
                    type="error"
                    showIcon
                    message={`ต้องเลือกวัตถุดิบทดแทนอีก ${pendingRows.length} รายการ`}
                    description="แถวสีแดงคือรหัสที่ไม่มีใน ERP กดปุ่ม “เลือกวัตถุดิบทดแทน” ในแถวนั้นให้ครบทุกรายการก่อน จึงจะดึงเข้าตาราง BOM ได้"
                  />
                ) : (
                  <Alert
                    type="success"
                    showIcon
                    message="วัตถุดิบครบทุกรายการแล้ว พร้อมดึงเข้าตาราง BOM"
                  />
                )}
                <Card>
                  <Table
                    bordered
                    size="small"
                    rowKey={(r) => r.rd_code}
                    dataSource={detail}
                    columns={detailColumns}
                    pagination={false}
                    scroll={{ y: "45vh" }}
                    rowClassName={(r) =>
                      isReady(r) ? "" : "rd-row-unmatched"
                    }
                  />
                </Card>
              </>
            )}
          </Space>
        </Spin>
      </Modal>

      {/* เลือกวัตถุดิบ ERP มาทดแทนทีละรายการ */}
      {pickingFor && (
        <ModalMaterial
          show={!!pickingFor}
          close={() => setPickingFor(null)}
          values={(v) => handleMaterialChoosed(v)}
        ></ModalMaterial>
      )}
    </>
  );
}
